import { Component } from '@theme/component';

/**
 * Base dropdown component that provides common dropdown functionality
 * 
 * @template {import('@theme/component').Refs} [Refs = {}]
 * @extends Component<Refs>
 */
export default class DropdownComponent extends Component {
  /** @type {boolean} */
  #isOpen = false;

  /** @type {HTMLElement | null} */
  #selectedOption = null;

  /** @type {string[]} */
  requiredRefs = ['trigger', 'dropdown', 'valueDisplay', 'options'];

  connectedCallback() {
    super.connectedCallback();
    this.setupComponent();
  }

  /**
   * Helper to check if element is HTMLElement
   * @param {unknown} el
   * @returns {el is HTMLElement}
   */
  #isHTMLElement(el) {
    return el instanceof HTMLElement;
  }

  /**
   * Gets available options
   * @returns {HTMLElement[]}
   */
  #getAvailableOptions() {
    return Array.from(this.querySelectorAll('.dropdown__option[data-available="true"]'));
  }

  setupComponent() {
    if (!this.refs.trigger || !this.refs.dropdown || !this.refs.valueDisplay) {
      console.warn('DropdownComponent: Required elements not found');
      return;
    }

    this.setupAccessibility();
    this.setupEventListeners();
    this.setInitialState();
  }

  setupAccessibility() {
    const { trigger, dropdown } = this.refs;
    
    if (this.#isHTMLElement(trigger)) {
      trigger.setAttribute('tabindex', '0');
      trigger.setAttribute('role', 'combobox');
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
    }

    if (this.#isHTMLElement(dropdown)) {
      dropdown.setAttribute('inert', '');
      dropdown.setAttribute('aria-hidden', 'true');
    }
  }

  setupEventListeners() {
    document.addEventListener('click', this.handleDocumentClick.bind(this));
    document.addEventListener('keydown', this.handleDocumentKeydown.bind(this));

    const { trigger, options } = this.refs;

    if (this.#isHTMLElement(trigger)) {
      trigger.addEventListener('click', this.handleTriggerClick.bind(this));
      trigger.addEventListener('keydown', this.handleTriggerKeydown.bind(this));
    }

    if (Array.isArray(options)) {
      options.forEach((option, index) => {
        if (this.#isHTMLElement(option)) {
          option.addEventListener('click', (e) => this.handleOptionClick(e, option));
          option.addEventListener('keydown', (e) => this.handleOptionKeydown(e, option, index));
        }
      });
    }
  }

  setInitialState() {
    const selectedOption = this.querySelector('.dropdown__option[aria-selected="true"]');
    if (this.#isHTMLElement(selectedOption)) {
      this.#selectedOption = selectedOption;
      this.updateDisplay();
    } else {
      this.#selectedOption = null;
    }
  }

  /**
   * @param {MouseEvent} e
   */
  handleDocumentClick(e) {
    if (!this.#isOpen || !this.#isHTMLElement(e.target)) return;
    if (!this.contains(e.target)) this.closeDropdown();
  }

  /**
   * @param {KeyboardEvent} e
   */
  handleDocumentKeydown(e) {
    if (e.key === 'Escape') this.closeDropdown();
  }

  /**
   * @param {MouseEvent} e
   */
  handleTriggerClick(e) {
    e.preventDefault();
    e.stopPropagation();
    this.toggleDropdown();
  }

  /**
   * @param {KeyboardEvent} e
   */
  handleTriggerKeydown(e) {
    const { key } = e;
    if (key === 'Enter' || key === ' ' || key === 'ArrowDown' || key === 'ArrowUp') {
      e.preventDefault();
      this.openDropdown();
      key === 'ArrowUp' ? this.focusLastOption() : this.focusFirstOption();
    }
  }

  /**
   * @param {MouseEvent} e
   * @param {HTMLElement} option
   */
  handleOptionClick(e, option) {
    e.preventDefault();
    e.stopPropagation();
    this.selectOption(option);
  }

  /**
   * @param {KeyboardEvent} e
   * @param {HTMLElement} option
   * @param {number} index
   */
  handleOptionKeydown(e, option, index) {
    const { key } = e;
    switch (key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.selectOption(option);
        break;
      case 'ArrowDown':
        e.preventDefault();
        this.focusNextOption(index);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.focusPreviousOption(index);
        break;
      case 'Escape':
        e.preventDefault();
        this.closeDropdown();
        if (this.#isHTMLElement(this.refs.trigger)) {
          this.refs.trigger.focus();
        }
        break;
    }
  }

  toggleDropdown() {
    this.#isOpen ? this.closeDropdown() : this.openDropdown();
  }

  openDropdown() {
    this.closeOtherDropdowns();
    this.#isOpen = true;
    this.classList.add('is-open');
    this.querySelector('.dropdown--custom')?.classList.add('is-open');
    
    const { trigger, dropdown } = this.refs;
    if (this.#isHTMLElement(trigger) && this.#isHTMLElement(dropdown)) {
      trigger.setAttribute('aria-expanded', 'true');
      dropdown.removeAttribute('inert');
      dropdown.removeAttribute('aria-hidden');
    }
  }

  closeDropdown() {
    this.#isOpen = false;
    this.classList.remove('is-open');
    this.querySelector('.dropdown--custom')?.classList.remove('is-open');
    const focusedOption = this.querySelector('.dropdown__option:focus');
    if (this.#isHTMLElement(focusedOption)) {
      focusedOption.blur();
    }
    
    const { trigger, dropdown } = this.refs;
    if (this.#isHTMLElement(trigger) && this.#isHTMLElement(dropdown)) {
      trigger.setAttribute('aria-expanded', 'false');
      dropdown.setAttribute('inert', '');
      dropdown.setAttribute('aria-hidden', 'true');
    }
  }


  closeOtherDropdowns() {
    const otherDropdowns = document.querySelectorAll(`${this.tagName.toLowerCase()}.is-open, .dropdown--custom.is-open`);
    otherDropdowns.forEach(dropdown => {
      if (dropdown !== this && dropdown instanceof DropdownComponent) {
        dropdown.closeDropdown();
      }
    });
  }

  /**
   * @param {HTMLElement} option
   */
  selectOption(option) {
    this.updateOptionSelection(option);
    this.updateDisplay();
    this.onOptionSelected(option);
    this.classList.add('has-selection');
    this.closeDropdown();
  }

  /**
   * @param {HTMLElement} selectedOption
   */
  updateOptionSelection(selectedOption) {
    if (Array.isArray(this.refs.options)) {
      this.refs.options.forEach(option => {
        if (this.#isHTMLElement(option)) {
          option.setAttribute('aria-selected', 'false');
          option.classList.remove('selected');
        }
      });
    }

    selectedOption.setAttribute('aria-selected', 'true');
    selectedOption.classList.add('selected');
    
    this.#selectedOption = selectedOption;
  }

  /**
   * Extracts text value from option, excluding sold-out spans
   * @param {HTMLElement} option
   * @returns {string}
   */
  #getOptionValue(option) {
    const value = option.getAttribute('data-value');
    if (value) return value;

    const optionText = option.querySelector('.dropdown__option-text');
    if (this.#isHTMLElement(optionText)) {
      const cloned = optionText.cloneNode(true);
      if (this.#isHTMLElement(cloned)) {
        cloned.querySelector('.sold-out')?.remove();
        return cloned.textContent?.trim() || '';
      }
    }
    return option.textContent?.trim() || '';
  }

  updateDisplay() {
    if (!this.#isHTMLElement(this.refs.valueDisplay) || !this.#selectedOption) return;

    const optionText = this.#selectedOption.querySelector('.dropdown__option-text');
    if (this.#isHTMLElement(optionText)) {
      const cloned = optionText.cloneNode(true);
      if (this.#isHTMLElement(cloned)) {
        this.refs.valueDisplay.innerHTML = cloned.innerHTML;
        return;
      }
    }
    this.refs.valueDisplay.textContent = this.#getOptionValue(this.#selectedOption);
  }

  /**
   * Called when an option is selected - to be overridden by subclasses
   * @param {HTMLElement} option
   */
  onOptionSelected(option) {
    if (this.refs.hiddenInput instanceof HTMLInputElement) {
      this.refs.hiddenInput.value = this.#getOptionValue(option);
    }
  }

  /**
   * Focuses an option by index
   * @param {number} index
   * @param {boolean} wrap - wrap to start/end if out of bounds
   */
  #focusOptionByIndex(index, wrap = false) {
    const options = this.#getAvailableOptions();
    if (options.length === 0) return;

    let targetIndex = index;
    if (wrap) {
      if (targetIndex < 0) targetIndex = options.length - 1;
      if (targetIndex >= options.length) targetIndex = 0;
    }

    const option = options[targetIndex];
    if (this.#isHTMLElement(option)) option.focus();
  }

  focusFirstOption() {
    this.#focusOptionByIndex(0);
  }

  focusLastOption() {
    const options = this.#getAvailableOptions();
    this.#focusOptionByIndex(options.length - 1);
  }

  /**
   * @param {number} currentIndex
   */
  focusNextOption(currentIndex) {
    this.#focusOptionByIndex(currentIndex + 1, true);
  }

  /**
   * @param {number} currentIndex
   */
  focusPreviousOption(currentIndex) {
    this.#focusOptionByIndex(currentIndex - 1, true);
  }

  /**
   * Gets the currently selected option
   * @returns {HTMLElement | null}
   */
  getSelectedOption() {
    return this.#selectedOption;
  }

  /**
   * Gets the current open state
   * @returns {boolean}
   */
  isOpen() {
    return this.#isOpen;
  }
}

if (!customElements.get('dropdown-component')) {
  customElements.define('dropdown-component', DropdownComponent);
}
